import React from 'react'
import { type ClassValue } from 'clsx'
import Link from 'next/link'
import { IconPlayerPlayFilled } from '@tabler/icons-react'
import type { Category, Post, Media } from '@/payload-types'

import { cn } from '@/utilities/ui'
import { slugToLink } from '@/utilities/slugToLink'
import { Locale } from '@/providers/Locale/types'
import { formatSeconds } from '@/utilities/formatDateTime'

import { MediaWithFallback, type MediaProps } from '@/components/frontend/MediaWithFallback'
import { Breadcrumbs } from '@/components/frontend/Breadcrumbs'
import { PublishedDateInfo } from '@/components/frontend/PublishedDateInfo'
import { FlatTitle } from '../FlatTitle'

export interface Props {
    classes?: Partial<Record<'title' | 'article' | 'description' | 'image', ClassValue>>
    post: Partial<Post>
    media: MediaProps
    titlePosition?: 'right' | 'bottom'
    useHero?: boolean
    mdFullImage?: boolean
    lgLeftImage?: boolean
    showPublishTime?: boolean
    locale: Locale
    showDescription?: boolean
    showCategories?: boolean
    showBreadcrumbs?: boolean
    hideImage?: boolean
    hideMobileImage?: boolean
    showPlayButton?: boolean
}

export const ArticleWithImage: React.FC<Props> = ({
    classes,
    media,
    useHero,
    hideMobileImage = true,
    mdFullImage,
    lgLeftImage,
    post,
    hideImage,
    titlePosition = 'right',
    showDescription = false,
    showPlayButton,
    showCategories,
    showBreadcrumbs,
    locale,
    showPublishTime,
}) => {
    const postTitle = post?.title
    const postDescription = post?.meta?.description
    const postUrl = slugToLink(post.slug)
    const category = post?.categories?.slice(-1)[0]
    const postCategory: Category = category && typeof category !== 'string' ? category : { id: '', title: 'unknown', updatedAt: '', createdAt: '' }
    const duration = (post.heroImage as Media)?.videoDuration

    return (
        <article
            className={cn(
                {
                    'flex flex-nowrap font-sans': true,
                    'flex-col': useHero || titlePosition === 'bottom',
                    'md:flex-col': mdFullImage,
                    'lg:flex-row': lgLeftImage,
                },
                classes?.article,
            )}
        >
            {!hideImage && media?.media && (
                <Link
                    href={postUrl}
                    className={cn(
                        {
                            'block flex-shrink-0': true,
                            'max-md:hidden': !useHero && hideMobileImage,
                            'w-1/3 mr-4': !useHero && titlePosition === 'right',
                            'lg:w-1/3': lgLeftImage,
                            'w-full mb-2': useHero || titlePosition === 'bottom',
                            'md:w-full': mdFullImage,
                        },
                        classes?.image,
                    )}
                >
                    <div className='relative h-full'>
                        <MediaWithFallback
                            media={media.media}
                            options={{
                                width: 600,
                                height: 400,
                                ...media.options,
                                className: cn(
                                    'aspect-video max-w-none w-full h-full object-cover object-top',
                                    { 'md:max-h-60': !useHero },
                                    media.options?.className,
                                ),
                                src: media.options?.src,
                                alt: media.options?.alt || '',
                            }}
                        />
                        {showPlayButton && (
                            <>
                                <div className='p-2 rounded-full bg-[#008CBA] absolute flex justify-center items-center top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-75'>
                                    <IconPlayerPlayFilled color='white' className='w-[14px] h-[14px]' />
                                </div>
                                {duration && (
                                    <div className='min-w-[50px] text-white font-roboto absolute right-[5px] bottom-[3px] text-center bg-[rgb(0,0,0)]/70'>
                                        {formatSeconds(duration)}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </Link>
            )}
            <div className='basis-0 flex-grow'>
                {showCategories && (
                    <div className='mb-2'>
                        <Link
                            data-ga={`[[&quot;Home page click&quot;,&quot;GROUP_ID&quot;,&quot;${postUrl}&quot;]]`}
                            className='text-accent uppercase font-sans hover:text-black font-semibold text-base'
                            href={postCategory.breadcrumbs?.slice(-1)[0]?.url || ''}
                        >
                            {postCategory.title}
                        </Link>
                    </div>
                )}
                {showBreadcrumbs && postCategory.breadcrumbs && <Breadcrumbs crumbs={postCategory.breadcrumbs} />}
                <Link href={postUrl} className='hover:text-neutral-500'>
                    <h4 className={cn({ 'text-xl': useHero, 'md:text-base': !useHero, 'font-bold mb-2 line-clamp-3': true }, classes?.title)}>
                        <FlatTitle heading={postTitle} />
                    </h4>
                    {showDescription && <p className={cn(classes?.description)}>{postDescription}</p>}
                    {showPublishTime && (
                        <PublishedDateInfo
                            showPublishTimeOnly={showPublishTime}
                            publishedAt={post.publishedAt}
                            updatedAt={post.updatedAt}
                            locale={locale}
                            showDisplayText={false}
                            className='text-sm font-semibold text-black/70 font-sans'
                        />
                    )}
                </Link>
            </div>
        </article>
    )
}
